import { supabase } from "../utils/supabase";
import { BlogPost, Comment } from "../types/blog";
import { getAllPosts as getStaticPosts } from "./posts";

const hasSupabaseConfig = Boolean(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY
);

async function withTimeout<T>(
  promise: PromiseLike<T>,
  timeoutMs = 2500
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("Supabase request timed out"));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// Helper function to convert snake_case to camelCase
function snakeToCamel(obj: unknown): unknown {
  if (!isRecord(obj)) {
    if (Array.isArray(obj)) {
      return obj.map(snakeToCamel);
    }

    return obj;
  }

  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    // Special case for raw_content to ensure it's included
    if (key === "raw_content") {
      acc["rawContent"] = obj[key];
      return acc;
    }

    // Special case for is_draft to ensure it's correctly mapped to isDraft
    if (key === "is_draft") {
      acc["isDraft"] = obj[key];
      return acc;
    }

    // Special case for content_type 
    if (key === "content_type") {
      acc["contentType"] = obj[key];
      return acc;
    }

    // Special case for embedded_images
    if (key === "embedded_images") {
      acc["embeddedImages"] = obj[key];
      return acc;
    }

    // Special case for editor_data
    if (key === "editor_data") {
      acc["editorData"] = obj[key];
      return acc;
    }

    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    acc[camelKey] = snakeToCamel(obj[key]);
    return acc;
  }, {});
}

// Helper function to convert camelCase to snake_case
function camelToSnake(obj: unknown): unknown {
  if (!isRecord(obj)) {
    if (Array.isArray(obj)) {
      return obj.map(camelToSnake);
    }

    return obj;
  }

  return Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    // Special case for rawContent to ensure it's included
    if (key === "rawContent") {
      acc["raw_content"] = obj[key];
      return acc;
    }

    // Special case for isDraft to ensure it's correctly mapped to is_draft
    if (key === "isDraft") {
      acc["is_draft"] = obj[key];
      return acc;
    }

    // Special case for contentType
    if (key === "contentType") {
      acc["content_type"] = obj[key];
      return acc;
    }

    // Special case for embeddedImages
    if (key === "embeddedImages") {
      acc["embedded_images"] = obj[key];
      return acc;
    }

    // Special case for editorData
    if (key === "editorData") {
      acc["editor_data"] = obj[key];
      return acc;
    }

    const snakeKey = key.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`
    );
    acc[snakeKey] = camelToSnake(obj[key]);
    return acc;
  }, {});
}

async function getCommentsByPostId(
  postIds: string[]
): Promise<Map<string, Comment[]>> {
  const commentsByPostId = new Map<string, Comment[]>();

  if (postIds.length === 0) {
    return commentsByPostId;
  }

  let response;

  try {
    response = await withTimeout(
      supabase
        .from("comments")
        .select("*")
        .in("post_id", postIds)
        .order("date", { ascending: true })
    );
  } catch (error) {
    console.warn("Falling back without comments:", error);
    return commentsByPostId;
  }

  const { data: comments, error } = response;

  if (error) {
    console.error("Error fetching comments:", error);
    return commentsByPostId;
  }

  (snakeToCamel(comments || []) as Array<Comment & { postId: string }>).forEach(
    (comment) => {
      const existingComments = commentsByPostId.get(comment.postId) || [];
      commentsByPostId.set(comment.postId, [...existingComments, comment]);
    }
  );

  return commentsByPostId;
}

// Get all posts from Supabase
export async function getAllPosts(): Promise<BlogPost[]> {
  if (!hasSupabaseConfig) {
    return getStaticPosts();
  }

  let response;

  try {
    response = await withTimeout(
      supabase.from("posts").select("*").order("date", { ascending: false })
    );
  } catch (error) {
    console.warn("Falling back to bundled posts:", error);
    return getStaticPosts();
  }

  const { data: posts, error: postsError } = response;

  if (postsError) {
    console.error("Error fetching posts:", postsError);
    return getStaticPosts();
  }

  // Convert snake_case to camelCase
  const camelCasePosts = snakeToCamel(posts || []) as BlogPost[];

  if (camelCasePosts.length === 0) {
    return getStaticPosts();
  }

  const commentsByPostId = await getCommentsByPostId(
    camelCasePosts.map((post) => post.id)
  );

  return camelCasePosts.map((post) => ({
    ...post,
    comments: commentsByPostId.get(post.id) || [],
  }));
}

// Get a single post by ID
export async function getPostById(id: string): Promise<BlogPost | null> {
  if (!hasSupabaseConfig) {
    return (await getStaticPosts()).find((post) => post.id === id) || null;
  }

  let response;

  try {
    response = await withTimeout(
      supabase.from("posts").select("*").eq("id", id).single()
    );
  } catch (error) {
    console.warn(`Falling back to bundled post for ${id}:`, error);
    return (await getStaticPosts()).find((post) => post.id === id) || null;
  }

  const { data: post, error: postError } = response;

  if (postError) {
    console.error(`Error fetching post with ID ${id}:`, postError);
    if (postError.code === 'PGRST116') {
      // Row not found
      return (await getStaticPosts()).find((post) => post.id === id) || null;
    }
    return (await getStaticPosts()).find((post) => post.id === id) || null;
  }

  // Convert snake_case to camelCase
  const camelCasePost = snakeToCamel(post) as BlogPost;

  // Fetch comments for this post
  const { data: comments, error: commentsError } = await supabase
    .from("comments")
    .select("*")
    .eq("post_id", id)
    .order("date", { ascending: true });

  if (commentsError) {
    console.error(`Error fetching comments for post ${id}:`, commentsError);
    return { ...camelCasePost, comments: [] };
  }

  // Convert snake_case to camelCase
  const camelCaseComments = snakeToCamel(comments || []) as Comment[];

  return { ...camelCasePost, comments: camelCaseComments };
}

// Create a new post
export async function createPost(post: BlogPost): Promise<BlogPost | null> {
  // Convert camelCase to snake_case
  const snakeCasePost = camelToSnake(post);

  const { data, error } = await supabase
    .from("posts")
    .insert([snakeCasePost])
    .select()
    .single();

  if (error) {
    console.error("Error creating post:", error);
    return null;
  }

  // Convert snake_case to camelCase
  const camelCasePost = snakeToCamel(data) as BlogPost;

  return { ...camelCasePost, comments: [] };
}

// Update an existing post
export async function updatePost(
  id: string,
  updates: Partial<Omit<BlogPost, "comments">>
): Promise<BlogPost | null> {
  try {
    // Convert camelCase to snake_case
    const snakeCaseUpdates = camelToSnake(updates);

    const { data, error } = await supabase
      .from("posts")
      .update(snakeCaseUpdates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating post with ID ${id}:`, error);
      return null;
    }

    if (!data) {
      console.error(`No data returned when updating post with ID ${id}`);
      return null;
    }

    // Convert snake_case to camelCase
    const camelCasePost = snakeToCamel(data) as BlogPost;

    // Fetch comments for this post
    const { data: comments, error: commentsError } = await supabase
      .from("comments")
      .select("*")
      .eq("post_id", id)
      .order("date", { ascending: true });

    if (commentsError) {
      console.error(`Error fetching comments for post ${id}:`, commentsError);
      return { ...camelCasePost, comments: [] };
    }

    // Convert snake_case to camelCase
    const camelCaseComments = snakeToCamel(comments || []) as Comment[];

    return { ...camelCasePost, comments: camelCaseComments };
  } catch (err) {
    console.error(`Exception updating post with ID ${id}:`, err);
    return null;
  }
}

// Delete a post
export async function deletePost(id: string): Promise<boolean> {
  const { error } = await supabase.from("posts").delete().eq("id", id);

  if (error) {
    console.error(`Error deleting post with ID ${id}:`, error);
    return false;
  }

  return true;
}

// Add a comment to a post
export async function addComment(
  postId: string,
  comment: Omit<Comment, "id">
): Promise<Comment | null> {
  // Convert camelCase to snake_case and add post_id
  const snakeCaseComment = camelToSnake({
    ...comment,
    postId, // This will be converted to post_id
  });

  const { data, error } = await supabase
    .from("comments")
    .insert([snakeCaseComment])
    .select()
    .single();

  if (error) {
    console.error(`Error adding comment to post with ID ${postId}:`, error);
    return null;
  }

  // Convert snake_case to camelCase
  return snakeToCamel(data) as Comment;
}

// Update likes count for a post
export async function updateLikes(
  postId: string,
  likes: number
): Promise<boolean> {
  const { error } = await supabase
    .from("posts")
    .update({ likes })
    .eq("id", postId);

  if (error) {
    console.error(`Error updating likes for post with ID ${postId}:`, error);
    throw new Error(`Failed to update likes: ${error.message}`);
  }

  return true;
}

export async function deleteComment(id: string): Promise<boolean> {
  const { error } = await supabase.from("comments").delete().eq("id", id);

  if (error) {
    console.error(`Error deleting comment with ID ${id}:`, error);
    throw new Error(`Failed to delete comment: ${error.message}`);
  }

  return true;
}
